/**
 * Spot Orders Tab Component
 *
 * Displays all spot orders with:
 * - Buy orders (purchases)
 * - Sell orders (sales)
 * - Order details including fees and USDT conversion
 */

import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  Collapse,
  IconButton,
  Avatar,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import type { StrategyInfo, Order, Fill } from '../../../types/strategy';
import { traderTheme as theme } from '../traderDashboardTheme';
import { getExchangeLogo, EXCHANGE_NAMES } from '../../../utils/exchangeUrls';

interface SpotOrdersTabProps {
  strategy: StrategyInfo;
}

type OrderFilter = 'all' | 'buy' | 'sell';

/**
 * Convert exchange name string to index for logo lookup
 */
function getExchangeIndex(exchangeName: string | undefined | null): number | undefined {
  if (!exchangeName) return undefined;
  const normalized = exchangeName.toLowerCase().replace('.', '').replace('-', '');
  const index = EXCHANGE_NAMES.findIndex(name => name === normalized);
  return index >= 0 ? index : undefined;
}

function formatDate(timestamp: number): string {
  if (!timestamp) return '-';
  const date = new Date(timestamp);
  return date.toLocaleString('ko-KR', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function formatPrice(price: number | null | undefined, quote: string): string {
  if (price == null) return '-';
  if (quote === 'KRW') {
    // Show natural decimals without padding (5.234 → 5.234, 5.1 → 5.1, 5 → 5)
    return `₩${price.toLocaleString('ko-KR', { minimumFractionDigits: 0, maximumFractionDigits: 8 })}`;
  }
  return `$${price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 8 })}`;
}

function formatVolume(value: number | null | undefined): string {
  if (value == null) return '-';
  return value.toLocaleString('en-US', {
    minimumFractionDigits: 4,
    maximumFractionDigits: 8,
  });
}

function formatUsdt(value: number | null | undefined): string {
  if (value == null) return '-';
  return `$${value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function getStatusColor(status: string): string {
  switch (status) {
    case 'Filled':
      return theme.profit;
    case 'PartiallyFilled':
      return theme.warning;
    case 'Pending':
    case 'Open':
      return theme.accent;
    case 'Cancelled':
    case 'Rejected':
    case 'Error':
      return theme.loss;
    default:
      return theme.text.secondary;
  }
}

/**
 * Calculate average price from fills (volume-weighted average)
 */
function calculateAvgPriceFromFills(fills: Fill[]): number {
  if (!fills || fills.length === 0) return 0;

  let totalVolume = 0;
  let totalValue = 0;

  for (const fill of fills) {
    totalVolume += fill.volume;
    totalValue += fill.price * fill.volume;
  }

  return totalVolume > 0 ? totalValue / totalVolume : 0;
}

/**
 * Get the best available price for display
 * Returns null if no valid price can be determined (e.g., market order without fills)
 */
function getDisplayPrice(order: Order): number | null {
  // If fills are available, calculate avg price from them
  if (order.fills && order.fills.length > 0) {
    return calculateAvgPriceFromFills(order.fills);
  }
  // Otherwise use executed_price if available
  if (order.executed_price && order.executed_price > 0) {
    return order.executed_price;
  }
  // For limit orders, use order price
  if (order.order_type === 'Limit' && order.price > 0) {
    return order.price;
  }
  // For market orders, try to calculate from total/volume
  if (order.total && order.total > 0 && order.executed_volume > 0) {
    return order.total / order.executed_volume;
  }
  // Can't determine price (market order without fills/total)
  return null;
}

interface OrderRowProps {
  order: Order;
  t: (key: string) => string;
}

const OrderRow: React.FC<OrderRowProps> = ({ order, t }) => {
  const [expanded, setExpanded] = useState(false);
  const isBuy = order.side === 'Bid';
  const sideColor = isBuy ? theme.profit : theme.loss;
  const hasFills = order.fills && order.fills.length > 0;
  const isMarketOrder = order.order_type === 'Market';

  // Get avg price from fills or fallback to order price (may be null for market orders)
  const avgPrice = getDisplayPrice(order);

  // Calculate USDT value using avg price or total
  const totalValue = order.total || (avgPrice ? avgPrice * order.executed_volume : 0);
  const usdtRate = order.quote_to_usdt_rate || 1.0;
  const usdtValue = totalValue * usdtRate;

  // Fee in USDT - check if fee asset matches quote currency
  let feeUsdt = 0;
  if (order.paid_fee && order.paid_fee > 0) {
    if (order.paid_fee_asset === 'USDT') {
      feeUsdt = order.paid_fee;
    } else if (order.paid_fee_asset === order.quote) {
      // Fee is in quote currency, convert to USDT
      feeUsdt = order.paid_fee * usdtRate;
    } else {
      // Fee is in different currency (e.g., BNB, base currency)
      // We can't accurately convert without the exchange rate for that currency
      // For now, we'll show the original fee amount in the detail line
      feeUsdt = 0;
    }
  }

  return (
    <Box>
      <Box
        onClick={() => hasFills && setExpanded(!expanded)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          py: 1.5,
          px: 2,
          borderBottom: expanded ? 'none' : `1px solid ${theme.border}`,
          cursor: hasFills ? 'pointer' : 'default',
          '&:hover': {
            backgroundColor: theme.bg.hover,
          },
        }}
      >
        {/* Expand Icon */}
        <Box sx={{ width: '4%' }}>
          {hasFills && (
            <IconButton size="small" sx={{ p: 0.25, color: theme.text.secondary }}>
              {expanded ? (
                <ExpandLessIcon sx={{ fontSize: 14 }} />
              ) : (
                <ExpandMoreIcon sx={{ fontSize: 14 }} />
              )}
            </IconButton>
          )}
        </Box>

        {/* Time */}
        <Box sx={{ width: '10%' }}>
          <Typography variant="caption" sx={{ color: theme.text.secondary, fontSize: '0.7rem' }}>
            {formatDate(order.created_at)}
          </Typography>
        </Box>

        {/* Side */}
        <Box sx={{ width: '7%' }}>
          <Chip
            label={isBuy ? t('traderDashboard.strategyDetail.orders.buy') : t('traderDashboard.strategyDetail.orders.sell')}
            size="small"
            sx={{
              backgroundColor: `${sideColor}20`,
              color: sideColor,
              fontSize: '0.65rem',
              height: 18,
              fontWeight: 600,
            }}
          />
        </Box>

        {/* Exchange */}
        <Box sx={{ width: '9%', display: 'flex', alignItems: 'center' }}>
          {getExchangeIndex(order.exchange) !== undefined && (
            <Avatar
              src={getExchangeLogo(getExchangeIndex(order.exchange)!)}
              sx={{ width: 20, height: 20, flexShrink: 0 }}
            />
          )}
        </Box>

        {/* Pair + Order ID */}
        <Box sx={{ width: '9%' }}>
          <Typography variant="caption" sx={{ color: theme.text.secondary, fontSize: '0.75rem' }}>
            {order.pair || `${order.base}/${order.quote}`}
          </Typography>
          <Typography
            variant="caption"
            onClick={(e) => {
              e.stopPropagation();
              navigator.clipboard.writeText(order.id);
            }}
            sx={{
              color: theme.text.muted,
              fontSize: '0.6rem',
              display: 'block',
              fontFamily: 'monospace',
              cursor: 'pointer',
              '&:hover': { color: theme.text.primary },
            }}
          >
            {order.id}
          </Typography>
        </Box>

        {/* Avg Price */}
        <Box sx={{ width: '13%', textAlign: 'right' }}>
          <Typography
            variant="caption"
            sx={{
              color: avgPrice ? theme.text.primary : theme.text.muted,
              fontFamily: 'monospace',
              fontSize: '0.75rem',
              fontStyle: avgPrice ? 'normal' : 'italic',
            }}
          >
            {avgPrice ? formatPrice(avgPrice, order.quote) : (isMarketOrder ? 'Market' : '-')}
          </Typography>
          {hasFills && (
            <Typography
              variant="caption"
              sx={{ color: theme.accent, fontSize: '0.6rem', display: 'block' }}
            >
              {order.fills!.length} fills
            </Typography>
          )}
        </Box>

        {/* Volume */}
        <Box sx={{ width: '11%', textAlign: 'right' }}>
          <Typography
            variant="caption"
            sx={{ color: theme.text.primary, fontFamily: 'monospace', fontSize: '0.75rem' }}
          >
            {formatVolume(order.executed_volume)}
          </Typography>
          {order.executed_volume !== order.volume && (
            <Typography
              variant="caption"
              sx={{ color: theme.text.muted, fontFamily: 'monospace', fontSize: '0.6rem', display: 'block' }}
            >
              / {formatVolume(order.volume)}
            </Typography>
          )}
        </Box>

        {/* Status */}
        <Box sx={{ width: '8%', textAlign: 'center' }}>
          <Typography
            variant="caption"
            sx={{ color: getStatusColor(order.status), fontSize: '0.7rem', fontWeight: 500 }}
          >
            {order.status}
          </Typography>
        </Box>

        {/* Fee */}
        <Box sx={{ width: '9%', textAlign: 'right' }}>
          {order.paid_fee && order.paid_fee > 0 ? (
            <>
              {feeUsdt > 0 && (
                <Typography
                  variant="caption"
                  sx={{ color: theme.text.secondary, fontFamily: 'monospace', fontSize: '0.7rem' }}
                >
                  {formatUsdt(feeUsdt)}
                </Typography>
              )}
              <Typography
                variant="caption"
                sx={{
                  color: feeUsdt > 0 ? theme.text.muted : theme.text.secondary,
                  fontFamily: 'monospace',
                  fontSize: feeUsdt > 0 ? '0.6rem' : '0.7rem',
                  display: 'block',
                }}
              >
                {order.paid_fee.toFixed(8)} {order.paid_fee_asset}
              </Typography>
            </>
          ) : (
            <Typography
              variant="caption"
              sx={{ color: theme.text.secondary, fontFamily: 'monospace', fontSize: '0.7rem' }}
            >
              -
            </Typography>
          )}
        </Box>

        {/* Total in Quote */}
        <Box sx={{ width: '10%', textAlign: 'right' }}>
          <Typography
            variant="caption"
            sx={{ color: theme.text.secondary, fontFamily: 'monospace', fontSize: '0.7rem' }}
          >
            {formatPrice(totalValue, order.quote)}
          </Typography>
        </Box>

        {/* Total (USDT) */}
        <Box sx={{ width: '10%', textAlign: 'right' }}>
          <Typography
            variant="body2"
            sx={{
              color: sideColor,
              fontFamily: 'monospace',
              fontWeight: 500,
              fontSize: '0.8rem',
            }}
          >
            {isBuy ? '-' : '+'}{formatUsdt(usdtValue)}
          </Typography>
          {usdtRate && usdtRate !== 1.0 && (
            <Typography
              variant="caption"
              sx={{
                color: theme.text.secondary,
                fontFamily: 'monospace',
                fontSize: '0.6rem',
                display: 'block',
              }}
            >
              @ {(1 / usdtRate).toFixed(2)}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Expandable Fills */}
      <Collapse in={expanded}>
        <Box
          sx={{
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
            borderBottom: `1px solid ${theme.border}`,
            px: 4,
            py: 1,
          }}
        >
          <Typography variant="caption" sx={{ color: theme.text.secondary, fontWeight: 600, mb: 1, display: 'block' }}>
            Fills ({order.fills?.length || 0})
          </Typography>
          {/* Fills table header */}
          <Box sx={{ display: 'flex', gap: 2, mb: 0.5, borderBottom: `1px solid ${theme.border}`, pb: 0.5 }}>
            <Typography variant="caption" sx={{ width: '5%', color: theme.text.muted, fontSize: '0.6rem' }}>#</Typography>
            <Typography variant="caption" sx={{ width: '25%', color: theme.text.muted, fontSize: '0.6rem' }}>{t('traderDashboard.strategyDetail.orders.price')}</Typography>
            <Typography variant="caption" sx={{ width: '20%', color: theme.text.muted, fontSize: '0.6rem' }}>{t('traderDashboard.strategyDetail.orders.volume')}</Typography>
            <Typography variant="caption" sx={{ width: '25%', color: theme.text.muted, fontSize: '0.6rem' }}>{t('traderDashboard.strategyDetail.orders.notional')}</Typography>
            <Typography variant="caption" sx={{ width: '25%', color: theme.text.muted, fontSize: '0.6rem' }}>{t('traderDashboard.strategyDetail.orders.fee')}</Typography>
          </Box>
          {/* Fills rows */}
          {order.fills?.map((fill, idx) => {
            const fillNotional = fill.price * fill.volume;
            return (
              <Box key={idx} sx={{ display: 'flex', gap: 2, py: 0.25 }}>
                <Typography variant="caption" sx={{ width: '5%', color: theme.text.muted, fontSize: '0.65rem' }}>
                  {idx + 1}
                </Typography>
                <Typography variant="caption" sx={{ width: '25%', color: theme.text.primary, fontFamily: 'monospace', fontSize: '0.7rem' }}>
                  {formatPrice(fill.price, order.quote)}
                </Typography>
                <Typography variant="caption" sx={{ width: '20%', color: theme.text.secondary, fontFamily: 'monospace', fontSize: '0.7rem' }}>
                  {formatVolume(fill.volume)}
                </Typography>
                <Typography variant="caption" sx={{ width: '25%', color: theme.text.primary, fontFamily: 'monospace', fontSize: '0.7rem' }}>
                  {formatPrice(fillNotional, order.quote)}
                </Typography>
                <Typography variant="caption" sx={{ width: '25%', color: theme.text.muted, fontFamily: 'monospace', fontSize: '0.65rem' }}>
                  {fill.fee && fill.fee > 0 ? `${fill.fee.toFixed(8)} ${fill.fee_currency || ''}` : '-'}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Collapse>
    </Box>
  );
};

export const SpotOrdersTab: React.FC<SpotOrdersTabProps> = ({ strategy }) => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<OrderFilter>('all');

  const orders = useMemo(() => {
    const allOrders = Object.values(strategy.execution_context.orders) as Order[];
    // Sort by created_at descending (newest first)
    return allOrders.sort((a, b) => b.created_at - a.created_at);
  }, [strategy.execution_context.orders]);

  const filteredOrders = useMemo(() => {
    if (filter === 'all') return orders;
    if (filter === 'buy') return orders.filter((o) => o.side === 'Bid');
    return orders.filter((o) => o.side === 'Ask');
  }, [orders, filter]);

  // Calculate totals
  const totals = useMemo(() => {
    const buyOrders = orders.filter((o) => o.side === 'Bid' && o.executed_volume > 0);
    const sellOrders = orders.filter((o) => o.side === 'Ask' && o.executed_volume > 0);

    const calculateOrderValue = (o: Order): number => {
      // Use total if available
      if (o.total && o.total > 0) {
        return o.total;
      }
      // Try to get price from fills or executed_price
      const price = getDisplayPrice(o);
      if (price && price > 0) {
        return price * o.executed_volume;
      }
      // Fallback to limit order price
      return o.price * o.executed_volume;
    };

    const totalBuyUsdt = buyOrders.reduce((sum, o) => {
      const total = calculateOrderValue(o);
      return sum + total * (o.quote_to_usdt_rate || 1.0);
    }, 0);

    const totalSellUsdt = sellOrders.reduce((sum, o) => {
      const total = calculateOrderValue(o);
      return sum + total * (o.quote_to_usdt_rate || 1.0);
    }, 0);

    const totalFees = orders.reduce((sum, o) => {
      if (!o.paid_fee || o.paid_fee <= 0) return sum;

      // Calculate fee in USDT based on fee currency
      if (o.paid_fee_asset === 'USDT') {
        return sum + o.paid_fee;
      } else if (o.paid_fee_asset === o.quote) {
        return sum + o.paid_fee * (o.quote_to_usdt_rate || 1.0);
      }
      // Fee in unknown currency - can't convert
      return sum;
    }, 0);

    const totalBuyVolume = buyOrders.reduce((sum, o) => sum + o.executed_volume, 0);
    const totalSellVolume = sellOrders.reduce((sum, o) => sum + o.executed_volume, 0);

    return {
      buyCount: buyOrders.length,
      sellCount: sellOrders.length,
      totalBuyUsdt,
      totalSellUsdt,
      totalFees,
      spotPnl: totalSellUsdt - totalBuyUsdt,
      totalBuyVolume,
      totalSellVolume,
    };
  }, [orders]);

  const handleFilterChange = (_event: React.MouseEvent<HTMLElement>, newFilter: OrderFilter | null) => {
    if (newFilter !== null) {
      setFilter(newFilter);
    }
  };

  return (
    <Box>
      {/* Summary Cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Paper
          sx={{
            p: 1.5,
            flex: 1,
            backgroundColor: theme.bg.secondary,
            border: `1px solid ${theme.border}`,
          }}
        >
          <Typography variant="caption" sx={{ color: theme.text.secondary, fontSize: '0.7rem' }}>
            {t('traderDashboard.strategyDetail.spotOrders.totalBuy')}
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: theme.text.primary, fontFamily: 'monospace', fontWeight: 600 }}
          >
            -{formatUsdt(totals.totalBuyUsdt)}
          </Typography>
          <Typography variant="caption" sx={{ color: theme.text.muted, fontSize: '0.65rem' }}>
            {totals.buyCount} {t('traderDashboard.strategyDetail.orders.orders')}
          </Typography>
        </Paper>
        <Paper
          sx={{
            p: 1.5,
            flex: 1,
            backgroundColor: theme.bg.secondary,
            border: `1px solid ${theme.border}`,
          }}
        >
          <Typography variant="caption" sx={{ color: theme.text.secondary, fontSize: '0.7rem' }}>
            {t('traderDashboard.strategyDetail.spotOrders.totalSell')}
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: theme.text.primary, fontFamily: 'monospace', fontWeight: 600 }}
          >
            +{formatUsdt(totals.totalSellUsdt)}
          </Typography>
          <Typography variant="caption" sx={{ color: theme.text.muted, fontSize: '0.65rem' }}>
            {totals.sellCount} {t('traderDashboard.strategyDetail.orders.orders')}
          </Typography>
        </Paper>
        <Paper
          sx={{
            p: 1.5,
            flex: 1,
            backgroundColor: theme.bg.secondary,
            border: `1px solid ${theme.border}`,
          }}
        >
          <Typography variant="caption" sx={{ color: theme.text.secondary, fontSize: '0.7rem' }}>
            {t('traderDashboard.strategyDetail.spotOrders.totalFees')}
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: theme.text.primary, fontFamily: 'monospace', fontWeight: 600 }}
          >
            {formatUsdt(totals.totalFees)}
          </Typography>
        </Paper>
        <Paper
          sx={{
            p: 1.5,
            flex: 1,
            backgroundColor: theme.bg.secondary,
            border: `1px solid ${totals.spotPnl >= 0 ? theme.profit : theme.loss}40`,
          }}
        >
          <Typography variant="caption" sx={{ color: theme.text.secondary, fontSize: '0.7rem' }}>
            {t('traderDashboard.strategyDetail.spotOrders.spotPnl')}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: totals.spotPnl >= 0 ? theme.profit : theme.loss,
              fontFamily: 'monospace',
              fontWeight: 600,
            }}
          >
            {totals.spotPnl >= 0 ? '+' : ''}{formatUsdt(totals.spotPnl)}
          </Typography>
        </Paper>
      </Box>

      {/* Filter */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle2" sx={{ color: theme.text.primary, fontWeight: 600 }}>
          {t('traderDashboard.strategyDetail.spotOrders.orderHistory')} ({filteredOrders.length})
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="caption" sx={{ color: theme.profit, fontFamily: 'monospace', fontSize: '0.75rem' }}>
            매수: {formatVolume(totals.totalBuyVolume)}
          </Typography>
          <Typography variant="caption" sx={{ color: theme.loss, fontFamily: 'monospace', fontSize: '0.75rem' }}>
            매도: {formatVolume(totals.totalSellVolume)}
          </Typography>
          <ToggleButtonGroup
            value={filter}
            exclusive
            onChange={handleFilterChange}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                color: theme.text.secondary,
                borderColor: theme.border,
                fontSize: '0.75rem',
                py: 0.5,
                px: 1.5,
                '&.Mui-selected': {
                  backgroundColor: `${theme.accent}20`,
                  color: theme.accent,
                },
              },
            }}
          >
            <ToggleButton value="all">{t('traderDashboard.strategyDetail.orders.all')}</ToggleButton>
            <ToggleButton value="buy">{t('traderDashboard.strategyDetail.orders.buy')}</ToggleButton>
            <ToggleButton value="sell">{t('traderDashboard.strategyDetail.orders.sell')}</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {/* Orders Table */}
      <Paper
        sx={{
          backgroundColor: theme.bg.secondary,
          border: `1px solid ${theme.border}`,
          borderRadius: 1,
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            py: 1,
            px: 2,
            backgroundColor: theme.bg.primary,
            borderBottom: `2px solid ${theme.border}`,
          }}
        >
          {/* Empty space for expand icon */}
          <Box sx={{ width: '4%' }} />
          <Typography variant="caption" sx={{ width: '10%', color: theme.text.primary, fontWeight: 600, fontSize: '0.75rem' }}>
            {t('traderDashboard.strategyDetail.orders.time')}
          </Typography>
          <Typography variant="caption" sx={{ width: '7%', color: theme.text.primary, fontWeight: 600, fontSize: '0.75rem' }}>
            {t('traderDashboard.strategyDetail.orders.side')}
          </Typography>
          <Typography variant="caption" sx={{ width: '9%', color: theme.text.primary, fontWeight: 600, fontSize: '0.75rem' }}>
            {/* Exchange logo column - no header text needed */}
          </Typography>
          <Typography variant="caption" sx={{ width: '9%', color: theme.text.primary, fontWeight: 600, fontSize: '0.75rem' }}>
            {t('traderDashboard.strategyDetail.orders.pair')}
          </Typography>
          <Typography variant="caption" sx={{ width: '13%', color: theme.text.primary, fontWeight: 600, fontSize: '0.75rem', textAlign: 'right' }}>
            {t('traderDashboard.strategyDetail.orders.avgPrice')}
          </Typography>
          <Typography variant="caption" sx={{ width: '11%', color: theme.text.primary, fontWeight: 600, fontSize: '0.75rem', textAlign: 'right' }}>
            {t('traderDashboard.strategyDetail.orders.volume')}
          </Typography>
          <Typography variant="caption" sx={{ width: '8%', color: theme.text.primary, fontWeight: 600, fontSize: '0.75rem', textAlign: 'center' }}>
            {t('traderDashboard.strategyDetail.orders.status')}
          </Typography>
          <Typography variant="caption" sx={{ width: '9%', color: theme.text.primary, fontWeight: 600, fontSize: '0.75rem', textAlign: 'right' }}>
            {t('traderDashboard.strategyDetail.orders.fee')}
          </Typography>
          <Typography variant="caption" sx={{ width: '10%', color: theme.text.primary, fontWeight: 600, fontSize: '0.75rem', textAlign: 'right' }}>
            Total
          </Typography>
          <Typography variant="caption" sx={{ width: '10%', color: theme.text.primary, fontWeight: 600, fontSize: '0.75rem', textAlign: 'right' }}>
            {t('traderDashboard.strategyDetail.orders.totalUsdt')}
          </Typography>
        </Box>

        {/* Rows */}
        <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
          {filteredOrders.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: theme.text.secondary }}>
                {t('traderDashboard.strategyDetail.orders.noOrders')}
              </Typography>
            </Box>
          ) : (
            filteredOrders.map((order) => (
              <OrderRow key={order.id} order={order} t={t} />
            ))
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default SpotOrdersTab;
